import { useContext, useEffect, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { DialogProps, FormControl, Stack } from '@mui/material';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { ClassContext } from '../../context/ClassContext';
import { ClassSessionInfo } from '../../context/types';
import { getUrls } from '../../utils/url';
import { ConfirmationDialog } from '../common/dialog/ConfirmationDialog';
import ControlledInput from '../forms/ControlledInput';

export type EditSessionDialogProps = DialogProps & {
    session?: ClassSessionInfo;
};

export const EditSessionDialog = (props: EditSessionDialogProps) => {
    const { open, onClose, session } = props;
    const { api } = getUrls();
    const { t } = useTranslation();
    const { sessions, setSessions, userUuid } = useContext(ClassContext);

    // It's probably overkill using yup and form hook for a single field, but this form will be expanded to include
    // other fields in the future, so it makes sense to start it off on the right path.
    const schema = yup.object({
        topic: yup.string().required(t('lti:field-required-topic'))
    });

    const defaultValues = {
        topic: session?.class_session_topic ?? ''
    };

    const [confirming, setConfirming] = useState(false);

    // Note - isValid must be deconstructed here or it will always be false:
    // https://github.com/react-hook-form/react-hook-form/issues/2755#issuecomment-962274624
    const {
        handleSubmit,
        control,
        formState: { isValid },
        reset
    } = useForm({ defaultValues, resolver: yupResolver(schema), shouldUnregister: true });

    const formRef = useRef<HTMLFormElement | null>(null);

    useEffect(() => {
        const defaults = {
            topic: session?.class_session_topic ?? ''
        };
        reset(defaults);
    }, [reset, session]);

    const handleClose = () => {
        onClose?.({}, 'backdropClick');
    };

    const onSubmit = async (data: any) => {
        if (!isValid) {
            return;
        }

        const params = {
            user_uuid: userUuid,
            session: {
                class_session_topic: data.topic,
                class_session_uuid: session?.class_session_uuid,
                class_uuid: session?.class_uuid
            }
        };

        const modifiedSession = sessions.find((s) => s.sid === session?.sid);

        if (!modifiedSession) {
            throw Error(`Failed to find session with id ${session?.sid}`);
        }
        modifiedSession.class_session_topic = data.topic;

        // Update context to reflect change in topic.
        const updatedSessions = sessions.map((s) => {
            if (s.sid === session?.sid) {
                return modifiedSession;
            }
            return s;
        });

        setSessions(updatedSessions);

        setConfirming(true);

        try {
            await axios.put(`${api}/v1/class/sessions`, params);
        } catch (e) {
            setSessions(sessions);
            console.error(e);
        }

        reset(data);
        setConfirming(false);
        handleClose();
    };

    const submitForm = () => {
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    };

    const handleEditDialogConfirmed = () => {
        submitForm();
    };

    return (
        <ConfirmationDialog
            id="edit-session"
            open={open}
            onClose={handleClose}
            title={t('calendar:edit-class-session')}
            confirmLabel={t('calendar:save')}
            onConfirm={handleEditDialogConfirmed}
            confirming={confirming}
            setConfirming={setConfirming}
        >
            <Stack>
                <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
                    <FormControl fullWidth>
                        <ControlledInput type="text" name="topic" control={control} label={t('class:topic')} />
                    </FormControl>
                </form>
            </Stack>
        </ConfirmationDialog>
    );
};
